import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Loader from 'components/Loader';
import Popup from 'components/Popup';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import useStudySession from 'hooks/useStudySession';
import { DISABLE_TEST_REPORT } from 'global/constants';
import { setError } from 'store/error';
import { createTimerWorker } from 'workers/createTimerWorker';
import DigitalACTModule from './Module';
import DigitalACTBreak from './Break';
import './index.css';

function DigitalACT() {
    const [isLastSection, setIsLastSection] = useState(false);
    const [selectedReview, setSelectedReview] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [direction, setDirection] = useState(true);
    const [testName, setTestName] = useState('');
    const [testReview, setTestReview] = useState(true);
    const [moduleId, setModuleId] = useState('');
    const [moduleName, setModuleName] = useState('');
    const [moduleNumber, setModuleNumber] = useState(0);
    const [moduleSubject, setModuleSubject] = useState(''); 
    const [modulePrompt, setModulePrompt] = useState('');
    const [breakAfter, setBreakAfter] = useState(false);
    const [moduleBreak, setModuleBreak] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [questionNumber, setquestionNumber] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [moduleTime, setModuleTime] = useState(null);
    const [questionTime, setQuestionTime] = useState(null);
    const [exitOpen, setExitOpen] = useState(false);
    const [submitOpen, setSubmitOpen] = useState(false);
    const [indexFilters, setIndexFilters] = useState({});

    const { user } = useAppSelector(state => state.user);
    const { organization } = useAppSelector(state => state.organization);
    const endTestRef = useOutsideClickHandler(() => setExitOpen(false));
    const submitRef = useOutsideClickHandler(() => setSubmitOpen(false));
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { testId } = useParams();

    const timerWorkerRef = useRef(null);

    useStudySession({
        activityType: 'test',
        activityIds: { studentTestId: testId },
    });

    const getNextModule = () => {
        setIsLoading(true);
        setModuleTime(null);
        axios.get(`tests/digital/act/${testId}/`)
            .then(res => {
                if (res.data.id) {
                    setIsLastSection(res.data.isLastSection);
                    setTestName(res.data.testName);
                    setTestReview(res.data.testReview);
                    setModuleId(res.data.id);
                    setModuleName(res.data.name);
                    setModuleNumber(res.data.number);
                    setModuleSubject(res.data.subject);
                    setModuleTime(res.data.duration);
                    setModulePrompt(res.data.prompt);
                    setModuleBreak(false);
                    setBreakAfter(res.data.breakAfter);
                    setQuestions(res.data.questions);
                    setAnswers(res.data.answers);
                    setquestionNumber(0);
                    setQuestionTime(0);

                    if (localStorage.getItem('autosave')) {
                        const autosave = JSON.parse(localStorage.getItem('autosave'));
                        if (autosave.moduleId === res.data.id) {
                            setAnswers(autosave.answers);

                            setModuleTime(autosave.moduleTime);
                        }
                    }

                    setIsLoading(false);
                }
                else {
                    if (!testReview || DISABLE_TEST_REPORT.includes(organization.alias)) {
                        // navigate('/tests/digital-act');
                        navigate('/tests');
                    }
                    else {
                        navigate(`/report/digital-act/${testId}`);
                    }
                    
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }

    const sendReport = () => {
        axios.post('debug/', {
            email: user.email,
            message: JSON.stringify({
                module: `${testName} ${moduleName}`,
                answers: answers,
                moduleTime: moduleTime
            })
        });
    }

    const beginTest = () => {
        setDirection(false);
        setQuestionTime(0);
        window.scrollTo(0, 0);
    }

    const closeDirection = () => {
        setDirection(false);
        setQuestionTime(0);
    }

    const autoSave = () => {
        localStorage.setItem('autosave', JSON.stringify({
            moduleId: moduleId,
            answers: answers,
            moduleTime: moduleTime
        }));
    }

    const navigateQuestion = (index: number) => {
        if (questionNumber > 0 && questionNumber <= questions.length) {
            let newAnswers = [...answers];
            newAnswers[questionNumber-1].timeSpent += questionTime;
            setAnswers(newAnswers);
        }
        setQuestionTime(0);
        setquestionNumber(index);

        autoSave();
    }

    const setAnswer = (answer: string) => {
        let newAnswers = [...answers];
        newAnswers[questionNumber-1].answer = answer;
        setAnswers(newAnswers);

        autoSave();
    }

    const setChoice = (answer: string) => {
        let newAnswers = [...answers];
        newAnswers[questionNumber-1].answer = answers[questionNumber-1].answer == answer
            ? ''
            : answer;

        newAnswers[questionNumber-1].crossedOut = newAnswers[questionNumber-1].crossedOut.filter(value => value !== answer);
        setAnswers(newAnswers);

        autoSave();
    }

    const manageExit = (exitOpen: boolean) => {
        setExitOpen(exitOpen)
        setSubmitOpen(false)
    }

    const manageSubmit = (submitOpen: boolean) => {
        setSubmitOpen(submitOpen)
        setExitOpen(false)
    }

    const markQuestion = () => {
        let newAnswers = [...answers];
        newAnswers[questionNumber-1].marked = !answers[questionNumber-1].marked;
        setAnswers(newAnswers);

        autoSave();
    }

    const crossOutChoice = (choiceId: string) => {
        let newAnswers = [...answers];
        if (newAnswers[questionNumber-1].crossedOut.includes(choiceId)) {
            newAnswers[questionNumber-1].crossedOut = newAnswers[questionNumber-1].crossedOut.filter(value => value !== choiceId);
        }
        else {
            newAnswers[questionNumber-1].crossedOut.push(choiceId);
        }
        if (choiceId === answers[questionNumber-1].answer) {
            newAnswers[questionNumber-1].answer = '';
        }
        setAnswers(newAnswers);

        autoSave();
    }

    const maskingChoice = (choiceId: string) => {
        let newAnswers = [...answers];
        if (newAnswers[questionNumber-1].masking.includes(choiceId)) {
            newAnswers[questionNumber-1].masking = newAnswers[questionNumber-1].masking.filter(value => value !== choiceId);
        }
        else {
            newAnswers[questionNumber-1].masking.push(choiceId);
        }
        // if (choiceId === answers[questionNumber-1].answer) {
        //     newAnswers[questionNumber-1].answer = '';
        // }
        setAnswers(newAnswers);

        autoSave();
    }

    const saveAnnotation = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
    }) => {
        let newAnswers = [...answers];
        const index = answers[questionNumber-1].annotations.findIndex(existing => (
            existing.passageId === annotation.passageId &&
            existing.startIndex === annotation.startIndex &&
            existing.endIndex === annotation.endIndex
        ));
        if (index >= 0) {
            newAnswers[questionNumber-1].annotations[index] = annotation;
        }
        else {
            newAnswers[questionNumber-1].annotations.push(annotation);
        }
        setAnswers(newAnswers);

        autoSave();
    }

    const deleteAnnotation = (annotation) => {
        let newAnswers = [...answers];
        const index = answers[questionNumber-1].annotations.findIndex(existing => (
            existing.passageId === annotation.passageId &&
            existing.startIndex === annotation.startIndex &&
            existing.endIndex === annotation.endIndex
        ));
        if (index >= 0) {
            newAnswers[questionNumber-1].annotations.splice(index, 1);
        }
        setAnswers(newAnswers);

        autoSave();
    }

    const clearAnnotation = () => {
        let newAnswers = [...answers];
        // const index = answers[questionNumber-1].annotations.findIndex(existing => (
        //     existing.passageId === annotation.passageId &&
        //     existing.startIndex === annotation.startIndex &&
        //     existing.endIndex === annotation.endIndex
        // ));
        // if (index >= 0) {
        //     newAnswers[questionNumber-1].annotations.splice(index, 1);
        // }
        newAnswers[questionNumber - 1].annotations = [];
        setAnswers(newAnswers);

        autoSave();
    }

    const handleIndexFilters = (filters: { 'All questions': number, 'Flagged': number, 'Answered': number, 'Unanswered': number }) => {
        setIndexFilters(filters);
    }

    const handleReview = (selectedReview: string) => {
        manageExit(false);
        setSelectedReview(selectedReview);
    }

    const handleSubmit = () => {
        setDirection(true);
        setSubmitOpen(false);
        setIsLoading(true);
        sendReport();
        axios.post(`tests/digital/submit/act/${testId}/${moduleId}/`, {
                answers: answers,
                timeLeft: moduleTime
            })
            .then(() => {
                localStorage.removeItem('autosave');
                if (breakAfter) {
                    setBreakAfter(false);
                    setModuleBreak(true);
                    setModuleTime(null);
                    setQuestionTime(null);
                    setIsLoading(false);
                }
                else {
                    getNextModule();
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }

    // Keep a ref to the latest handleSubmit so the timer worker (whose onmessage
    // closure is wired only once) always submits LIVE answers, not a stale snapshot.
    const handleSubmitRef = useRef(handleSubmit);
    useEffect(() => { handleSubmitRef.current = handleSubmit; });

    const handleSaveAndExit = () => {
        setIsLoading(true);
        axios.post(`tests/digital/save/act/${testId}/${moduleId}/`, {
                answers: answers,
                timeLeft: moduleTime
            })
            .then(() => {
                // exit
                navigate(-1);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }
    
    const handleDiscardAndExit = () => {
        localStorage.removeItem('autosave');
        navigate(-1);
    }

    useEffect(() => {
        getNextModule();
    }, []);

    useEffect(() => {
        if (isLoading || direction) return;

        if (timerWorkerRef.current) {
            timerWorkerRef.current.postMessage({ type: 'stop' });
            timerWorkerRef.current.terminate();
            timerWorkerRef.current = null;
        }

        timerWorkerRef.current = createTimerWorker();

        timerWorkerRef.current.onmessage = (event) => {
            if (event.data === 'tick') {
                setModuleTime(prev => {
                    if (prev === null) return prev;
                    if (prev <= 1) {
                        handleSubmitRef.current();
                        return 0;
                    }
                    return prev - 1;
                });
                setQuestionTime(prev => {
                    if (prev === null) return prev;
                    return prev + 1;
                });
            }
        };

        timerWorkerRef.current.postMessage({ type: 'start', delay: 1000 });

        return () => {
            if (timerWorkerRef.current) {
                timerWorkerRef.current.postMessage({ type: 'stop' });
                timerWorkerRef.current.terminate();
                timerWorkerRef.current = null;
            }
        };
    }, [isLoading, direction]);

    if (isLoading) return <Loader />;

    return (
        <div className='digitalACT' onCopy={(event) => { event.preventDefault() }}>
            <Helmet>
                <title>
                    {testName ? testName : 'Digital ACT Practice Test'} - {organization.name}
                </title>
            </Helmet>
            {exitOpen &&
                <Popup
                    title='Exit Test Confirmation'
                    primaryButtonText='SAVE AND EXIT'
                    primaryButtonOnClick={handleSaveAndExit}
                    secondaryButtonText='DISCARD AND EXIT'
                    secondaryButtonOnClick={handleDiscardAndExit}
                    thirdButtonText='Cancel'
                    thirdButtonOnClick={() => manageExit(false)}
                    manageExit={manageExit}
                    question='Are you sure you want to exit the test?'
                    indexFilters={indexFilters}
                    handleReview={value => handleReview(value)}
                    innerRef={endTestRef}
                />
            }
            {submitOpen && (
                    isLastSection
                    ?<Popup
                        title={`End Test Confirmation`}
                        primaryButtonText={`YES, END THE TEST`}
                        primaryButtonOnClick={handleSubmit}
                        secondaryButtonText={`NO, DON'T END THE TEST`}
                        secondaryButtonOnClick={()=> manageSubmit(false)}
                        manageExit={manageSubmit}
                        question='Are you sure you want to end the test?'
                        indexFilters={indexFilters}
                        handleReview={value => handleReview(value)}
                        innerRef={submitRef}
                    />
                    : <Popup
                        title={`End ${moduleSubject.replace('ACT ','').replace('Mathematics', 'Math')} Confirmation`}
                        primaryButtonText={`YES, SUBMIT ${moduleSubject.replace('ACT ','').replace('Mathematics', 'Math').toUpperCase()}`}
                        primaryButtonOnClick={handleSubmit}
                        secondaryButtonText={`NO, DON'T SUBMIT ${moduleSubject.replace('ACT ','').replace('Mathematics', 'Math').toUpperCase()}`}
                        secondaryButtonOnClick={()=> manageSubmit(false)}
                        manageExit={manageSubmit}
                        question={`Are you sure you want to submit ${moduleSubject.replace('ACT ','').replace('Mathematics', 'Math')} subject?`}
                        indexFilters={indexFilters}
                        handleReview={value => handleReview(value)}
                        innerRef={submitRef}
                    />
                )
            }
            <div className={moduleNumber && moduleBreak
                ? 'digitalACT__box--break'
                : 'digitalACT__box'
            }>
                {moduleBreak 
                    ?   <DigitalACTBreak
                            endBreak={getNextModule}
                        />
                    :   <DigitalACTModule
                            testName={testName}
                            moduleName={moduleName}
                            subject={moduleSubject}
                            timeLeft={moduleTime}
                            isUntimed={moduleTime===null? true: false}
                            direction={direction}
                            prompt={modulePrompt}
                            questions={questions}
                            questionNumber={questionNumber}
                            answers={answers}
                            selectedReview = {selectedReview}
                            handleIndexFilters={value => handleIndexFilters(value)}
                            closeDirection={closeDirection}
                            beginTest={beginTest}
                            manageExit={manageExit}
                            manageSubmit={manageSubmit}
                            markQuestion={markQuestion}
                            crossOutChoice={crossOutChoice}
                            maskingChoice={maskingChoice}
                            saveAnnotation={saveAnnotation}
                            deleteAnnotation={deleteAnnotation}
                            clearAnnotation={clearAnnotation}
                            handleChange={value => setAnswer(value)}
                            handleSelect={value => setChoice(value)}
                            navigateQuestion={navigateQuestion}
                            handleSaveAndExit={handleSaveAndExit}
                            handleDiscardAndExit={handleDiscardAndExit}
                            handleSubmit={handleSubmit}
                        />
                    
                }
            </div>
        </div>
    )
}

export default DigitalACT;
